// TROIA-V3-FRONTEND/src/modules/crm/seller-dashboard/components/StoreRankingCard.tsx
import React from 'react';
import { Link } from 'react-router-dom';
import type { RankingEntry, RankingResponse } from '@troia-v3/types';

import { Avatar, AvatarFallback, AvatarImage, DashCard } from '@/shared/components/ui';
import { Icon } from '@/shared/icons';
import { formatPeriodLabel } from '@/shared/utils/gamificationPeriod';

import { formatPts } from '../lib/sellerFormat';

import '../seller-dashboard.css';

interface StoreRankingCardProps {
  ranking?: RankingResponse;
  unitName?: string;
  userId: string;
}

const initials = (name: string): string => name.split(' ').filter(Boolean).slice(0, 2).map((p) => p[0]?.toUpperCase() ?? '').join('');
const posClass = (pos: number): string => pos === 1 ? 'seller-rk-pos seller-rk-pos--gold' : pos === 2 ? 'seller-rk-pos seller-rk-pos--silver' : pos === 3 ? 'seller-rk-pos seller-rk-pos--bronze' : 'seller-rk-pos';

export const RankingRow: React.FC<{ entry: RankingEntry; me: boolean }> = ({ entry, me }) => (
  <div data-testid={`seller-ranking-row-${entry.id}`}
    className={`grid grid-cols-[32px_32px_1fr_70px] gap-3 items-center px-4 py-2 border-t first:border-t-0 ${me ? 'seller-rk-row--me' : ''}`}
    style={{ borderColor: 'var(--theme-card-border)' }}>
    <span className={posClass(entry.position)}>{entry.position}</span>
    <Avatar className="w-8 h-8" isSquircle>
      {entry.avatar && <AvatarImage src={entry.avatar} alt={entry.name} />}
      <AvatarFallback>{initials(entry.name)}</AvatarFallback>
    </Avatar>
    <span className="font-semibold text-sm truncate" style={{ color: 'var(--theme-card-text)' }}>
      {entry.name}
      {me && <span className="ml-2 text-[10px] font-bold rounded-full px-[7px] py-px align-[1px]" style={{ color: 'var(--theme-primary)', backgroundColor: 'var(--theme-primary-background)' }}>você</span>}
    </span>
    <span className="font-bold text-right" style={{ fontVariantNumeric: 'tabular-nums', color: 'var(--theme-card-text)' }}>{formatPts(entry.points)} pts</span>
  </div>
);

/** "Ranking da loja" — mockup 07 (.mini-rk): top 3 + minha linha (se fora do top 3); variante C = card vazio. */
export const StoreRankingCard: React.FC<StoreRankingCardProps> = ({ ranking, unitName, userId }) => {
  const periodLabel = ranking ? formatPeriodLabel(ranking.period, ranking.periodKey) : '';
  if (!ranking || ranking.visibility === 'only_me') {
    return (
      <DashCard icon="award" title="Ranking" subtitle={periodLabel} testId="seller-ranking" noPadding>
        <div className="px-5 py-10 text-center text-[13px]" style={{ color: 'var(--theme-muted-text)' }} data-testid="seller-ranking-hidden">
          <div className="w-11 h-11 rounded-xl mx-auto mb-2.5 flex items-center justify-center" style={{ backgroundColor: 'var(--theme-surface-sunken)' }}><Icon name="award" size={18} /></div>
          A empresa optou por mostrar só os seus números.<br />Seus pontos e nível continuam contando.
        </div>
      </DashCard>
    );
  }
  const top = ranking.entries.slice(0, 3);
  const me = ranking.me;
  /**
   * 4 linhas, como o mockup 07 (`miniRank`): top 3 + a 4ª. Quando o vendedor
   * está FORA do pódio, a 4ª linha é a DELE (é a informação que ele abre o
   * card para ver) — senão é o #4 do recorte. Antes o card mostrava 3 linhas
   * num dia em que o vendedor estava no pódio, deixando um buraco visual em
   * relação ao mockup.
   */
  const outsideTop = Boolean(me && !top.some((e) => e.id === userId));
  const fourth = outsideTop ? me : ranking.entries[3];
  const gapText = me?.gapToNext !== undefined && me.position > 1
    ? <>Faltam <b style={{ color: 'var(--theme-card-text)' }}>{formatPts(me.gapToNext)} pts</b> para alcançar o #{me.position - 1}.</>
    : null;
  return (
    <DashCard icon="award" title="Ranking da loja" subtitle={`${unitName ? `${unitName} · ` : ''}${periodLabel}`} testId="seller-ranking" noPadding
      headerRight={<Link to="/crm/ranking" className="inline-flex items-center gap-1 text-xs font-semibold" style={{ color: 'var(--theme-primary)' }} data-testid="seller-ranking-full">Ver completo <Icon name="arrow-right" size={14} /></Link>}>
      <div className="py-1.5">
        {top.map((e) => <RankingRow key={e.id} entry={e} me={e.id === userId} />)}
        {fourth && <RankingRow entry={fourth} me={fourth.id === userId} />}
      </div>
      {gapText && <div className="px-4 pb-2 pt-1 text-xs" style={{ color: 'var(--theme-muted-text)' }} data-testid="seller-ranking-gap">{gapText}</div>}
    </DashCard>
  );
};
StoreRankingCard.displayName = 'StoreRankingCard';
